/**
 * Neo-Brutalist Product Cards - AJAX Cart Integration
 * Handles add to cart, 3D models, and AR functionality
 */

class NeoProductCards {
  constructor() {
    // Initialize performance optimizations
    this.cartDrawerRef = null;
    this.cartDrawerReadyPromise = null;
    this.debounceTimers = new Map();
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupAOS();
    this.setupPerformanceOptimizations();
    console.log('✅ Neo Product Cards initialized with enhanced cart integration');
  }

  // Setup performance optimizations for multiple product cards
  setupPerformanceOptimizations() {
    // Cache cart drawer reference on first access
    this.getCachedCartDrawer();
    
    // Setup shared cart drawer ready promise to avoid multiple simultaneous checks
    this.getSharedCartDrawerReadyPromise();
    
    // Add debounced cart update method
    this.debouncedCartUpdate = this.debounce('cartUpdate', this.triggerCartUpdate.bind(this), 100);
  }

  // Cached cart drawer reference to avoid repeated queries
  getCachedCartDrawer() {
    if (!this.cartDrawerRef || !this.cartDrawerRef.isConnected) {
      this.cartDrawerRef = document.querySelector('cart-drawer');
    }
    return this.cartDrawerRef;
  }

  // Shared cart drawer ready promise to prevent multiple simultaneous readiness checks
  getSharedCartDrawerReadyPromise() {
    if (!this.cartDrawerReadyPromise) {
      this.cartDrawerReadyPromise = this.waitForCartDrawerReady();
      
      // Reset promise after it resolves (successful or failed)
      this.cartDrawerReadyPromise.finally(() => {
        setTimeout(() => {
          this.cartDrawerReadyPromise = null;
        }, 1000);
      });
    }
    return this.cartDrawerReadyPromise;
  }

  // Generic debounce utility for performance optimization
  debounce(key, func, delay) {
    return (...args) => {
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key));
      }
      
      const timer = setTimeout(() => {
        func(...args);
        this.debounceTimers.delete(key);
      }, delay);
      
      this.debounceTimers.set(key, timer);
    };
  }

  bindEvents() {
    // AJAX Add to Cart form handler
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('neo-card__cart-form')) {
        e.preventDefault();
        this.handleAddToCart(e.target);
      }
    });

    // Alternative click handler for add to cart buttons
    document.addEventListener('click', (e) => {
      // Handle add to cart button clicks directly
      if (e.target.classList.contains('neo-card__add-btn') || e.target.closest('.neo-card__add-btn')) {
        const button = e.target.classList.contains('neo-card__add-btn') ? e.target : e.target.closest('.neo-card__add-btn');
        const form = button.closest('.neo-card__cart-form');
        
        if (form && !button.disabled) {
          e.preventDefault();
          this.handleAddToCart(form);
          return;
        }
      }

      // Product Title Links - Track clicks
      if (e.target.classList.contains('neo-card__title-link')) {
        this.trackProductView(e.target);
      }
    });
  }

  // AJAX Add to Cart Handler
  async handleAddToCart(form) {
    const submitBtn = form.querySelector('.neo-card__add-btn');
    const productId = form.dataset.productId;
    const variantId = form.querySelector('.neo-card__variant-id').value;
    const quantity = form.querySelector('input[name="quantity"]').value || 1;

    if (!variantId) {
      this.showError('Product variant not found');
      return;
    }

    // Set loading state
    this.setLoadingState(submitBtn, true);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      // Make AJAX request to Shopify
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const result = await response.json();

      if (response.ok) {
        // Success feedback
        this.showSuccess(submitBtn);
        this.animateCardSuccess(form.closest('.neo-card'));
        
        // Trigger cart update for dropdown
        this.triggerCartUpdate(result);
        
        // Analytics tracking
        this.trackAddToCart(productId, variantId, quantity);
        
      } else {
        console.error('❌ Add to cart failed:', result);
        throw new Error(result.message || result.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('❌ Add to cart error:', error);
      this.showError(error.message || 'Failed to add to cart');
    } finally {
      // Remove loading state
      setTimeout(() => {
        this.setLoadingState(submitBtn, false);
      }, 1000);
    }
  }

  // Set button loading state
  setLoadingState(button, loading) {
    if (loading) {
      button.setAttribute('data-loading', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('data-loading');
      button.disabled = false;
    }
  }

  // Show success feedback with comprehensive null safety checks
  showSuccess(button) {
    console.log('🔧 [PRODUCT CARD] Showing success feedback for button:', button);
    
    // Get elements with null safety checks
    const textElement = button.querySelector('.neo-card__add-text');
    const iconElement = button.querySelector('.neo-card__add-icon');
    
    // Validate required elements exist
    if (!textElement) {
      console.warn('⚠️ [PRODUCT CARD] Text element (.neo-card__add-text) not found in button');
      // Fallback: update button background only
      button.style.background = '#28A745';
      setTimeout(() => {
        button.style.background = '';
      }, 2000);
      return;
    }
    
    // Store original text safely
    const originalText = textElement.textContent || 'Add to Cart';
    console.log('🔧 [PRODUCT CARD] Original text:', originalText);
    
    // Update text element (guaranteed to exist)
    textElement.textContent = 'Added!';
    button.style.background = '#28A745';
    
    // Update icon element only if it exists
    if (iconElement) {
      console.log('🔧 [PRODUCT CARD] Icon element found, updating to success state');
      iconElement.textContent = '✅';
    } else {
      console.warn('⚠️ [PRODUCT CARD] Icon element (.neo-card__add-icon) not found, skipping icon update');
    }
    
    // Revert after 2 seconds with null safety
    setTimeout(() => {
      console.log('🔧 [PRODUCT CARD] Reverting success feedback');
      
      // Revert text (guaranteed to exist)
      if (textElement) {
        textElement.textContent = originalText;
      }
      
      // Revert icon only if it exists
      if (iconElement) {
        iconElement.textContent = '🛒';
      }
      
      // Revert button background
      button.style.background = '';
    }, 2000);
    
    console.log('✅ [PRODUCT CARD] Success feedback applied successfully');
  }

  // Show error feedback
  showError(message) {
    // Simple error notification
    const notification = document.createElement('div');
    notification.className = 'neo-error-notification';
    notification.innerHTML = `
      <div class="neo-error-content">
        <span class="neo-error-icon">❌</span>
        <span class="neo-error-message">${message}</span>
      </div>
    `;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Show with animation
    setTimeout(() => {
      notification.classList.add('neo-error-notification--visible');
    }, 100);
    
    // Remove after 4 seconds
    setTimeout(() => {
      notification.classList.remove('neo-error-notification--visible');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 4000);
  }

  // Animate card success
  animateCardSuccess(card) {
    card.classList.add('neo-card--added');
    setTimeout(() => {
      card.classList.remove('neo-card--added');
    }, 600);
  }

  // Enhanced cart drawer update with progressive retry and context-aware timing
  async triggerCartUpdate(addToCartResponse) {
    console.log('🔧 [PRODUCT CARD] Starting enhanced cart drawer update...', addToCartResponse);
    
    try {
      // First update cart icon bubble count immediately
      this.updateCartIconCount(addToCartResponse);
      
      // Get context-aware delay based on page type and number of product cards
      const contextualDelay = this.getContextualDelay();
      console.log('🔧 [PRODUCT CARD] Using contextual delay:', contextualDelay + 'ms');
      
      // Wait for cart drawer to be ready with progressive retry
      const isReady = await this.waitForCartDrawerReady();
      
      if (!isReady) {
        console.error('❌ [PRODUCT CARD] Cart drawer failed readiness check after all attempts');
        this.showFallbackNotification();
        return;
      }
      
      // Get cart drawer reference (validated by readiness check)
      const cartDrawer = document.querySelector('cart-drawer');
      
      // Apply contextual delay before proceeding
      await new Promise(resolve => setTimeout(resolve, contextualDelay));
      
      // Fetch fresh cart sections for cart drawer
      const sectionsToUpdate = ['cart-drawer'];
      const sectionsUrl = `${window.location.pathname}?sections=${sectionsToUpdate.join(',')}`;
      
      console.log('🔧 [PRODUCT CARD] Fetching sections from:', sectionsUrl);
      
      const response = await fetch(sectionsUrl);
      if (!response.ok) {
        throw this.createClassifiedError('NETWORK_ERROR', `Failed to fetch sections: ${response.status}`);
      }
      
      const sections = await response.json();
      console.log('🔧 [PRODUCT CARD] Sections received:', Object.keys(sections));
      
      // Validate section content
      if (!sections['cart-drawer']) {
        throw this.createClassifiedError('VALIDATION_ERROR', 'cart-drawer section missing from response');
      }
      
      // Create a parsedState object that cart drawer expects
      const parsedState = {
        sections: sections
      };
      
      console.log('🔧 [PRODUCT CARD] Calling cartDrawer.renderContents with:', parsedState);
      
      // Update cart drawer with error handling
      try {
        cartDrawer.renderContents(parsedState);
      } catch (renderError) {
        throw this.createClassifiedError('RENDER_ERROR', `Cart drawer render failed: ${renderError.message}`);
      }
      
      // Update cart icon count directly
      this.updateCartIconCount(addToCartResponse);
      
      // Also trigger standard cart events for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { item: addToCartResponse }
      }));
      
      // Dispatch custom event to notify other components
      document.dispatchEvent(new CustomEvent('cart:drawer:updated', {
        detail: { 
          source: 'product-card',
          sections: sections,
          item: addToCartResponse 
        }
      }));
      
      console.log('✅ [PRODUCT CARD] Cart drawer updated successfully');
      
    } catch (error) {
      console.error('❌ [PRODUCT CARD] Cart drawer update failed:', error);
      this.handleCartDrawerError(error, addToCartResponse);
    }
  }

  // Progressive retry mechanism for cart drawer readiness
  async waitForCartDrawerReady(maxAttempts = 4, baseDelay = 150) {
    console.log('🔧 [PRODUCT CARD] Checking cart drawer readiness...');
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`🔧 [PRODUCT CARD] Readiness attempt ${attempt}/${maxAttempts}`);
      
      if (this.isCartDrawerReady()) {
        console.log('✅ [PRODUCT CARD] Cart drawer is ready!');
        return true;
      }
      
      if (attempt < maxAttempts) {
        const delay = Math.floor(baseDelay * Math.pow(1.5, attempt - 1));
        console.log(`🔧 [PRODUCT CARD] Cart drawer not ready, waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    console.error('❌ [PRODUCT CARD] Cart drawer failed all readiness checks');
    return false;
  }

  // Comprehensive cart drawer readiness validation
  isCartDrawerReady() {
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (!cartDrawer) {
      console.log('🔧 [PRODUCT CARD] Cart drawer element not found');
      return false;
    }
    
    if (!cartDrawer.isConnected) {
      console.log('🔧 [PRODUCT CARD] Cart drawer not connected to DOM');
      return false;
    }
    
    if (typeof cartDrawer.renderContents !== 'function') {
      console.log('🔧 [PRODUCT CARD] Cart drawer renderContents method not available');
      return false;
    }
    
    if (cartDrawer.renderContents.length === undefined) {
      console.log('🔧 [PRODUCT CARD] Cart drawer renderContents method signature invalid');
      return false;
    }
    
    if (!cartDrawer.querySelector) {
      console.log('🔧 [PRODUCT CARD] Cart drawer DOM methods not available');
      return false;
    }
    
    if (cartDrawer.hasAttribute('data-initializing')) {
      console.log('🔧 [PRODUCT CARD] Cart drawer still initializing');
      return false;
    }
    
    return true;
  }

  // Context-aware delay calculation based on page type and card count
  getContextualDelay() {
    const productCardCount = document.querySelectorAll('.neo-card').length;
    const isCollectionPage = productCardCount > 1;
    const isHomepage = document.body.classList.contains('template-index');
    
    console.log('🔧 [PRODUCT CARD] Context detection:', {
      productCardCount,
      isCollectionPage,
      isHomepage
    });
    
    if (isHomepage && productCardCount > 6) {
      return 500; // Homepage with many cards
    }
    if (isCollectionPage && productCardCount > 8) {
      return 400; // Large collection
    }
    if (isCollectionPage) {
      return 250; // Standard collection
    }
    return 100; // Product page or single card
  }

  // Create classified error for better error handling
  createClassifiedError(type, message) {
    const error = new Error(message);
    error.classification = type;
    return error;
  }

  // Enhanced error handling with classification and specific recovery strategies
  handleCartDrawerError(error, addToCartResponse) {
    const errorType = error.classification || this.classifyError(error);
    
    console.error(`❌ [PRODUCT CARD] Classified error: ${errorType}`, error.message);
    
    switch (errorType) {
      case 'NOT_READY':
        console.log('🔧 [PRODUCT CARD] Attempting recovery with delayed retry...');
        setTimeout(() => this.triggerCartUpdate(addToCartResponse), 1000);
        break;
        
      case 'NETWORK_ERROR':
        console.log('🔧 [PRODUCT CARD] Network error, showing fallback notification');
        this.updateCartIconCount(addToCartResponse);
        this.showFallbackNotification();
        break;
        
      case 'TIMEOUT':
        console.log('🔧 [PRODUCT CARD] Timeout error, refreshing cart and showing fallback');
        this.updateCartIconCount(addToCartResponse);
        this.showFallbackNotification();
        break;
        
      case 'VALIDATION_ERROR':
      case 'RENDER_ERROR':
        console.log('🔧 [PRODUCT CARD] Validation/render error, using fallback approach');
        this.updateCartIconCount(addToCartResponse);
        this.showFallbackNotification();
        break;
        
      default:
        console.log('🔧 [PRODUCT CARD] Unknown error, using default fallback');
        this.updateCartIconCount(addToCartResponse);
        this.showFallbackNotification();
    }
  }

  // Classify error type based on error message and context
  classifyError(error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('not found') || message.includes('not available')) {
      return 'NOT_READY';
    }
    if (message.includes('fetch') || message.includes('network') || message.includes('failed to fetch')) {
      return 'NETWORK_ERROR';
    }
    if (message.includes('timeout') || message.includes('exceeded')) {
      return 'TIMEOUT';
    }
    if (message.includes('validation') || message.includes('missing') || message.includes('invalid')) {
      return 'VALIDATION_ERROR';
    }
    if (message.includes('render') || message.includes('contents')) {
      return 'RENDER_ERROR';
    }
    
    return 'UNKNOWN';
  }

  // Fallback notification when drawer update fails but item was added
  showFallbackNotification() {
    const notification = document.createElement('div');
    notification.className = 'neo-cart-fallback-notification';
    notification.innerHTML = `
      <div class="neo-cart-fallback-content">
        <span>✅ Ürün sepete eklendi!</span>
        <button onclick="this.parentElement.parentElement.remove()" style="margin-left: 10px; background: none; border: none; font-size: 18px; cursor: pointer;">×</button>
      </div>
    `;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      background: #28a745;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      animation: slideInRight 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 3000);
  }

  // Helper method to update cart icon count immediately
  updateCartIconCount(cartData) {
    try {
      // Fetch fresh cart data to get accurate count
      fetch('/cart.js')
        .then(response => response.json())
        .then(freshCartData => {
          const cartBubble = document.querySelector('.cart-count-bubble');
          if (cartBubble) {
            const countElement = cartBubble.querySelector('span[aria-hidden="true"]');
            if (countElement) {
              countElement.textContent = freshCartData.item_count;
            }
            cartBubble.style.display = freshCartData.item_count > 0 ? 'flex' : 'none';
          }
        })
        .catch(error => console.error('Error updating cart count:', error));
    } catch (error) {
      console.error('Error in updateCartIconCount:', error);
    }
  }


  // Track product view
  trackProductView(link) {
    const productUrl = link.getAttribute('href');
    const productTitle = link.querySelector('.neo-card__title-text')?.textContent?.trim() ||
                        link.getAttribute('aria-label')?.replace('View Product: ', '') ||
                        'Unknown Product';
    
    // Add analytics tracking here if needed
    if (typeof gtag !== 'undefined') {
      gtag('event', 'view_item', {
        currency: 'TRY',
        value: 0,
        items: [{
          item_id: productUrl.split('/').pop(),
          item_name: productTitle
        }]
      });
    }
  }

  // Track add to cart
  trackAddToCart(productId, variantId, quantity) {
    // Add analytics tracking here if needed
    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_to_cart', {
        currency: 'TRY',
        value: 0, // You can get the price from the card
        items: [{
          item_id: productId,
          item_variant: variantId,
          quantity: quantity
        }]
      });
    }
  }

  // Setup AOS animations if available
  setupAOS() {
    if (typeof AOS !== 'undefined') {
      // Refresh AOS when new cards are added
      setTimeout(() => {
        AOS.refresh();
      }, 100);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.neoProductCards = new NeoProductCards();
});

// CSS for error notifications and 3D modal (injected via JS)
const neoProductCardStyles = `
  /* Error Notification */
  .neo-error-notification {
    position: fixed;
    top: 2rem;
    right: 2rem;
    z-index: 999999;
    background: #FF3B30;
    color: white;
    padding: 1.5rem 2rem;
    border: 3px solid #2C3E50;
    border-radius: 8px;
    box-shadow: 6px 6px 0px rgba(44, 62, 80, 0.4);
    transform: translateX(100%) rotate(3deg);
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    max-width: 400px;
  }

  .neo-error-notification--visible {
    transform: translateX(0) rotate(1deg);
  }

  .neo-error-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .neo-error-icon {
    font-size: 1.5rem;
  }

  .neo-error-message {
    font-weight: 700;
    font-size: 1.4rem;
  }

  /* 3D Model Modal */
  .neo-3d-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999999;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  .neo-3d-modal--visible {
    opacity: 1;
    visibility: visible;
  }

  .neo-3d-modal__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }

  .neo-3d-modal__content {
    background: #FFFEF7;
    border: 4px solid #2C3E50;
    border-radius: 12px;
    box-shadow: 8px 8px 0px rgba(44, 62, 80, 0.4);
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow: hidden;
    transform: scale(0.8);
    transition: transform 0.3s ease;
  }

  .neo-3d-modal--visible .neo-3d-modal__content {
    transform: scale(1);
  }

  .neo-3d-modal__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 3px solid #2C3E50;
    background: linear-gradient(135deg, #4ECDC4, #44C7B9);
  }

  .neo-3d-modal__title {
    font-size: 1.8rem;
    font-weight: 900;
    color: #2C3E50;
    margin: 0;
  }

  .neo-3d-modal__close {
    width: 4rem;
    height: 4rem;
    background: #FF3B30;
    color: white;
    border: 3px solid #2C3E50;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 3px 3px 0px rgba(44, 62, 80, 0.3);
  }

  .neo-3d-modal__close:hover {
    background: #D70015;
    transform: scale(1.1);
    box-shadow: 4px 4px 0px rgba(44, 62, 80, 0.4);
  }

  .neo-3d-modal__close-icon {
    width: 2rem;
    height: 2rem;
  }

  .neo-3d-modal__viewer {
    height: 500px;
    position: relative;
  }

  .neo-3d-model-viewer {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .neo-3d-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }

  .neo-3d-spinner {
    font-size: 4rem;
    animation: spin 2s linear infinite;
    margin-bottom: 1rem;
  }

  /* AR Product Modal */
  .neo-ar-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999999;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  .neo-ar-modal--visible {
    opacity: 1;
    visibility: visible;
  }

  .neo-ar-modal__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }

  .neo-ar-modal__content {
    background: #FFFEF7;
    border: 4px solid #2C3E50;
    border-radius: 12px;
    box-shadow: 8px 8px 0px rgba(44, 62, 80, 0.4);
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    transform: scale(0.8);
    transition: transform 0.3s ease;
  }

  .neo-ar-modal--visible .neo-ar-modal__content {
    transform: scale(1);
  }

  .neo-ar-modal__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 3px solid #2C3E50;
    background: linear-gradient(135deg, #FF6B9D, #FF5A8B);
  }

  .neo-ar-modal__title {
    font-size: 1.8rem;
    font-weight: 900;
    color: #2C3E50;
    margin: 0;
  }

  .neo-ar-modal__close {
    width: 4rem;
    height: 4rem;
    background: #FF3B30;
    color: white;
    border: 3px solid #2C3E50;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 3px 3px 0px rgba(44, 62, 80, 0.3);
  }

  .neo-ar-modal__close:hover {
    background: #D70015;
    transform: scale(1.1);
    box-shadow: 4px 4px 0px rgba(44, 62, 80, 0.4);
  }

  .neo-ar-modal__close-icon {
    width: 2rem;
    height: 2rem;
  }

  .neo-ar-modal__body {
    padding: 2rem;
  }

  .neo-ar-modal__product {
    display: flex;
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .neo-ar-modal__image {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border: 3px solid #2C3E50;
    border-radius: 8px;
    box-shadow: 4px 4px 0px rgba(44, 62, 80, 0.2);
  }

  .neo-ar-modal__info {
    flex: 1;
  }

  .neo-ar-modal__info h4 {
    font-size: 1.6rem;
    font-weight: 900;
    color: #2C3E50;
    margin: 0 0 1rem;
  }

  .neo-ar-modal__price {
    font-size: 1.8rem;
    font-weight: 900;
    color: #FF6B35;
    margin: 0 0 1rem;
  }

  .neo-ar-modal__description {
    color: rgba(44, 62, 80, 0.8);
    line-height: 1.5;
    margin: 0;
  }

  .neo-ar-modal__ar-section {
    text-align: center;
    border-top: 3px solid #2C3E50;
    padding-top: 2rem;
  }

  .neo-ar-modal__ar-btn {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 2.5rem;
    background: #FF6B9D;
    color: #2C3E50;
    border: 4px solid #2C3E50;
    border-radius: 8px;
    font-size: 1.6rem;
    font-weight: 900;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 6px 6px 0px #E55A8C;
    transform: rotate(-1deg);
    text-decoration: none;
  }

  .neo-ar-modal__ar-btn:hover {
    background: #FF5A8B;
    transform: rotate(-1deg) translate(-3px, -4px) scale(1.05);
    box-shadow: 10px 10px 0px #E55A8C;
  }

  .neo-ar-modal__ar-icon {
    font-size: 2rem;
  }

  .neo-ar-modal__ar-note {
    color: rgba(44, 62, 80, 0.6);
    font-style: italic;
    margin-top: 1rem;
    font-size: 1.2rem;
  }

  @media screen and (max-width: 768px) {
    .neo-3d-modal__overlay,
    .neo-ar-modal__overlay {
      padding: 1rem;
    }
    
    .neo-3d-modal__header,
    .neo-ar-modal__header {
      padding: 1rem 1.5rem;
    }
    
    .neo-3d-modal__title,
    .neo-ar-modal__title {
      font-size: 1.4rem;
    }
    
    .neo-3d-modal__viewer {
      height: 400px;
    }

    .neo-ar-modal__body {
      padding: 1.5rem;
    }

    .neo-ar-modal__product {
      flex-direction: column;
      gap: 1rem;
    }

    .neo-ar-modal__image {
      width: 100px;
      height: 100px;
      align-self: center;
    }

    .neo-ar-modal__ar-btn {
      padding: 1.2rem 2rem;
      font-size: 1.4rem;
    }

    .neo-error-notification {
      top: 1rem;
      right: 1rem;
      left: 1rem;
      max-width: none;
    }
  }
`;

// Inject styles
const neoProductCardStyleSheet = document.createElement('style');
neoProductCardStyleSheet.textContent = neoProductCardStyles;
document.head.appendChild(neoProductCardStyleSheet);

// Export for potential external usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NeoProductCards;
}
